/**
 * The hero's decorative backdrop.
 *
 * Purely presentational: it renders nothing a screen reader can see, carries no
 * business state, and can be removed entirely without affecting a single
 * scheduling behaviour. It exists to make the hero feel alive, and everything in
 * it is drawn from Lunchly's existing palette — the navy field, the pink accent
 * and the teal counterpoint already used elsewhere in the shell. No new hues are
 * introduced.
 *
 * The imagery is deliberately about the product rather than generic decoration:
 * a faint scheduling grid, curved paths running between time markers with
 * occasional dots travelling along them (a lunch being assigned), pulsing
 * slot markers, and floating time chips.
 *
 * Five layers, back to front:
 *   1. atmosphere — large, very soft radial glows that drift
 *   2. grid       — the scheduling grid and its timeline ruler
 *   3. paths      — SVG assignment paths, markers and travelling dots
 *   4. chips      — floating time / status fragments
 *   5. spotlight  — the focus glow sitting behind the headline
 *
 * Performance rules observed throughout: only `transform` and `opacity` are
 * animated, the travelling-dot count is capped at four, blur comes from
 * gradient falloff rather than large `filter: blur()` radii, and the pointer
 * parallax is coalesced into a single `requestAnimationFrame`. Every listener
 * and frame is released by `destroy()`.
 */

import { el, svgEl } from '../dom.js';

/** Viewport-independent coordinate space for the path artwork. */
const VIEW_WIDTH = 1200;
const VIEW_HEIGHT = 800;

/**
 * Assignment paths. Each is a gentle curve from the edge of the stage towards
 * the centre, as though a shift were being drawn towards its lunch slot.
 * `dot` says whether a travelling particle runs along it — only two of the four
 * carry one at a time, so the stage never looks busy.
 */
const PATHS = [
  { id: 'a', d: 'M -60 208 C 214 138, 372 262, 648 196', dot: true, delay: '1.5s', duration: '13s' },
  { id: 'b', d: 'M 1260 296 C 1006 244, 858 372, 604 306', dot: true, delay: '4.2s', duration: '15s' },
  { id: 'c', d: 'M -60 566 C 246 520, 430 628, 712 556', dot: true, delay: '7.6s', duration: '14s' },
  { id: 'd', d: 'M 1260 648 C 992 604, 826 706, 548 664', dot: true, delay: '10.4s', duration: '16s' },
];

/** Pulsing slot markers, positioned on the path curves. */
const MARKERS = [
  { cx: 648, cy: 196, r: 5, delay: '0.9s' },
  { cx: 604, cy: 306, r: 4, delay: '1.6s' },
  { cx: 712, cy: 556, r: 5, delay: '2.3s' },
  { cx: 548, cy: 664, r: 4, delay: '3.0s' },
  { cx: 214, cy: 168, r: 3, delay: '2.7s' },
  { cx: 1006, cy: 262, r: 3, delay: '3.4s' },
];

/**
 * Floating fragments. Positioned in the margins with `inset` percentages so they
 * sit around the content rather than over it; the narrow breakpoints hide the
 * ones that would otherwise crowd the headline.
 */
const CHIPS = [
  { text: '12:00', className: 'hero-fx__chip--a' },
  { text: '12:30', className: 'hero-fx__chip--b' },
  { text: '13:00', className: 'hero-fx__chip--c' },
  { text: '14:30', className: 'hero-fx__chip--d' },
  { text: 'Coverage protected', className: 'hero-fx__chip--e hero-fx__chip--wide' },
  { text: 'Lunch assigned', className: 'hero-fx__chip--f hero-fx__chip--wide' },
];

/** True when the visitor has asked for less movement. */
function reducedMotionPreferred() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
}

/**
 * True for touch-first devices. Parallax that follows a finger is worse than no
 * parallax, and the extra listeners are wasted work on exactly the hardware
 * least able to spare it.
 */
function coarsePointer() {
  return window.matchMedia?.('(pointer: coarse)')?.matches === true;
}

/** Build the SVG path artwork. */
function buildStage() {
  const defs = svgEl('defs', {}, [
    svgEl('linearGradient', { id: 'heroFxLine', x1: '0', y1: '0', x2: '1', y2: '0' }, [
      svgEl('stop', { offset: '0', 'stop-color': '#42cfbd', 'stop-opacity': '0' }),
      svgEl('stop', { offset: '0.45', 'stop-color': '#42cfbd', 'stop-opacity': '0.55' }),
      svgEl('stop', { offset: '1', 'stop-color': '#f22b9d', 'stop-opacity': '0.65' }),
    ]),
    svgEl('radialGradient', { id: 'heroFxMarker' }, [
      svgEl('stop', { offset: '0', 'stop-color': '#ff86cb', 'stop-opacity': '0.95' }),
      svgEl('stop', { offset: '1', 'stop-color': '#cc127b', 'stop-opacity': '0' }),
    ]),
  ]);

  const lines = PATHS.map((path) => svgEl('path', {
    class: `hero-fx__line hero-fx__line--${path.id}`,
    d: path.d,
    fill: 'none',
    stroke: 'url(#heroFxLine)',
    'stroke-width': '1.25',
    'stroke-linecap': 'round',
    style: `--hero-fx-delay:${path.delay}`,
  }));

  const markers = MARKERS.flatMap((marker) => [
    svgEl('circle', {
      class: 'hero-fx__marker-halo',
      cx: marker.cx,
      cy: marker.cy,
      r: marker.r * 3.4,
      fill: 'url(#heroFxMarker)',
      style: `--hero-fx-delay:${marker.delay}`,
    }),
    svgEl('circle', {
      class: 'hero-fx__marker',
      cx: marker.cx,
      cy: marker.cy,
      r: marker.r,
      fill: '#ff86cb',
      style: `--hero-fx-delay:${marker.delay}`,
    }),
  ]);

  // Motion-path dots live inside the SVG so they use its user coordinate
  // system: they stay glued to their curve at every viewport size, with no
  // JavaScript keeping them in sync.
  const dots = PATHS.filter((path) => path.dot).map((path) => svgEl('circle', {
    class: 'hero-fx__dot',
    r: '3.4',
    fill: '#f22b9d',
    style: `--hero-fx-path:path('${path.d}');--hero-fx-delay:${path.delay};--hero-fx-duration:${path.duration}`,
  }));

  return svgEl(
    'svg',
    {
      class: 'hero-fx__stage',
      viewBox: `0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`,
      preserveAspectRatio: 'xMidYMid slice',
      'aria-hidden': 'true',
      focusable: 'false',
    },
    [defs, ...lines, ...markers, ...dots],
  );
}

/**
 * Create the backdrop.
 *
 * @returns {{ element: HTMLElement, destroy: Function }} `element` is inert
 *   decoration (`aria-hidden`, `pointer-events: none`) that the hero places
 *   behind its content.
 */
export function createHeroBackdrop() {
  const element = el('div', {
    class: 'hero-fx',
    attrs: { 'aria-hidden': 'true' },
  }, [
    // Layer 1 — atmosphere.
    el('div', { class: 'hero-fx__wash' }),
    el('div', { class: 'hero-fx__glow hero-fx__glow--pink' }),
    el('div', { class: 'hero-fx__glow hero-fx__glow--teal' }),
    el('div', { class: 'hero-fx__glow hero-fx__glow--deep' }),
    // Layer 2 — scheduling grid and timeline ruler.
    el('div', { class: 'hero-fx__grid' }),
    el('div', { class: 'hero-fx__ruler' }),
    // Layer 3 — assignment paths, markers and travelling dots.
    buildStage(),
    // Layer 4 — floating time fragments.
    el('div', { class: 'hero-fx__chips' }, CHIPS.map((chip) => el('span', {
      class: `hero-fx__chip ${chip.className}`,
      text: chip.text,
    }))),
    // Layer 5 — the focus glow behind the headline.
    el('div', { class: 'hero-fx__spotlight' }),
  ]);

  /* --- Entrance ---------------------------------------------------------- */

  // The sequence is CSS; this only flips it on after the first frame so the
  // browser has painted the initial state. It runs once — nothing re-adds the
  // class, so the entrance never replays while the visitor is reading.
  let entranceFrame = window.requestAnimationFrame(() => {
    entranceFrame = 0;
    element.classList.add('is-live');
  });

  /* --- Pointer parallax -------------------------------------------------- */

  let parallaxFrame = 0;
  let pointerHost = null;
  let handleMove = null;
  let handleLeave = null;

  if (!reducedMotionPreferred() && !coarsePointer()) {
    let targetX = 0;
    let targetY = 0;

    const apply = () => {
      parallaxFrame = 0;
      element.style.setProperty('--hero-fx-px', targetX.toFixed(4));
      element.style.setProperty('--hero-fx-py', targetY.toFixed(4));
    };

    // Reading the pointer position is free; the layout read and the style write
    // are deferred to one frame so a fast mouse cannot thrash layout.
    handleMove = (event) => {
      const host = pointerHost ?? element.parentElement;
      if (!host) return;
      const rect = host.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      targetX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      targetY = ((event.clientY - rect.top) / rect.height) * 2 - 1;
      if (!parallaxFrame) parallaxFrame = window.requestAnimationFrame(apply);
    };

    handleLeave = () => {
      targetX = 0;
      targetY = 0;
      if (!parallaxFrame) parallaxFrame = window.requestAnimationFrame(apply);
    };
  }

  /**
   * Attach pointer tracking to the hero element. Called by the hero once the
   * backdrop is in the tree, because the movement is measured against the hero's
   * box rather than the backdrop's own.
   */
  function observePointer(host) {
    if (!host || !handleMove) return;
    pointerHost = host;
    host.addEventListener('pointermove', handleMove, { passive: true });
    host.addEventListener('pointerleave', handleLeave, { passive: true });
  }

  function destroy() {
    if (entranceFrame) window.cancelAnimationFrame(entranceFrame);
    if (parallaxFrame) window.cancelAnimationFrame(parallaxFrame);
    entranceFrame = 0;
    parallaxFrame = 0;
    if (pointerHost && handleMove) {
      pointerHost.removeEventListener('pointermove', handleMove);
      pointerHost.removeEventListener('pointerleave', handleLeave);
    }
    pointerHost = null;
    element.remove();
  }

  return { element, observePointer, destroy };
}
